/**
 * Wall bookkeeping for a room split.
 *
 * Mirrors what the merge does in the other direction (`deriveMergedWalls`):
 * a wall entirely in one piece is carried VERBATIM — same id, texture, height,
 * `structureWallId` — so its fixtures, locks and textures need no remap; a
 * boundary wall the chain tees into is clipped where the chain's faces meet
 * it, and the LONGER piece keeps the original id (the merge's keep-one-id
 * rule). A clipped wall's finish regions are clipped with it.
 *
 * The chain contributes one face per stretch to each piece. Each is built over
 * the piece's own EDGE on that stretch's face line, so a room wall lies exactly
 * on its room's boundary rather than on the wall's nominal full length — which
 * is what stops a snapped endpoint that runs a few centimetres into a flank's
 * body from leaving an overshooting face. The gap close builds its one face
 * the same way (`buildPartitionFace`).
 */

const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { clipWall } = require('../room-merge/stitch');
const {
  wallDir,
  wallFrame,
  wallEndpoints,
  roomFloorPolygonXZ,
  distanceToPolygonBoundary,
  distanceToSegment,
} = require('./geometry');
const { stretchFrame } = require('./chain');
const { clipWallTextureRegions } = require('./regions-remap');
const {
  profileUnderCeiling,
  profileOfOutline,
  outlineCorners,
  profileTop,
} = require('../geometry/wallTopProfile');
const { DEFAULT_WALL_TRIM } = require('../millwork');

/**
 * Deep clone of a wall record, used wherever a room wall is carried into a
 * side VERBATIM.
 *
 * "Verbatim" means same id, texture, height and `structureWallId` - it does NOT
 * mean the same object. Regenerating the split rooms' ceilings rewrites wall
 * tops in place: a 2.4 m partition in a 2.5 m room fits the ceiling to the new
 * shorter wall, and every other wall's top corners are pulled down to it
 * (measured on 4A40D2CE: 1.1116 -> 1.0116, `dimensions.y` 2.5 -> 2.4). Hand the
 * side the caller's own wall object and that rewrite lands in the SOURCE room
 * too, so `splitRoom`'s "inputs are not mutated" contract quietly fails. The
 * merge guards the same hazard the same way (`stitch.js: cloneWall`).
 *
 * `structuredClone` rather than a JSON round trip, which turns `-0` into `0`
 * and a `Date` into a string - both of which a wall record carries.
 */
const cloneWall = (w) =>
  typeof structuredClone === 'function'
    ? structuredClone(w)
    : JSON.parse(JSON.stringify(w));

const ON_LINE_EPS_M = 0.005; // a polygon edge within 5 mm of the face line is ON it
const MIN_PIECE_M = 0.1; // a clipped survivor shorter than this is dropped
const LONG_FACE_DOT = 0.985; // |dir·wallDir| above this ⇒ a long face, not an end cap
// A boundary hit this close to a room wall's line cuts that wall. Looser than
// ON_LINE_EPS_M because a scanned room wall can sit a centimetre or two off
// its polygon (16 of 2408 corpus room walls sit 1-30 mm off it).
const HIT_ON_WALL_M = 0.02;
const MIN_FACE_M = 1e-6; // a face edge shorter than this is numerical dust

/** Coordinate of a point along the frame normal. */
function u(p, frame) {
  return p.x * frame.n.x + p.z * frame.n.z;
}

/**
 * Where a wall must be cut, as fractions of `bl → br` strictly inside (0, 1):
 * at each of the cut's boundary hits on it, and at each source-outline vertex
 * on it — where the room's outline leaves the wall's line (a box carved out of
 * the room's side leaves the wall running across its mouth), so the stretch of
 * wall on each side of that point is judged on its own.
 *
 * @returns {number[]} sorted fractions
 */
function cutsAlongWall(ends, cut, sourceRing) {
  const dx = ends.br.x - ends.bl.x;
  const dz = ends.br.z - ends.bl.z;
  const len2 = dx * dx + dz * dz;
  if (!(len2 > 0)) return [];
  const len = Math.sqrt(len2);
  const along = (p) => ((p.x - ends.bl.x) * dx + (p.z - ends.bl.z) * dz) / len2;
  const onLine = (p) =>
    Math.abs((p.x - ends.bl.x) * dz - (p.z - ends.bl.z) * dx) / len <=
    HIT_ON_WALL_M;
  const interior = (t) => t * len > MIN_FACE_M && (1 - t) * len > MIN_FACE_M;

  const cuts = [...cut.hits, ...sourceRing]
    .filter(onLine)
    .map(along)
    .filter(interior)
    .sort((a, b) => a - b);
  return cuts;
}

/** Adjacent runs of one wall going to the same piece, joined back up — never
 *  a stretch on an outline onto one that is not. */
function joinRuns(runs) {
  const out = [];
  for (const r of runs) {
    const last = out[out.length - 1];
    if (
      last &&
      last.side === r.side &&
      last.onOutline === r.onOutline &&
      Math.abs(last.s1 - r.s0) < 1e-12
    ) {
      last.s1 = r.s1;
      last.len += r.len;
    } else {
      out.push({ ...r });
    }
  }
  return out;
}

/**
 * Which piece a stretch of a room wall belongs to, and whether it lies on an
 * outline at all.
 *
 *   - on the outline a chain end stands on (`cut.gaps`), nearer it than either
 *     piece → 0 (dropped): what the chain's end stands on is in no room;
 *   - in or on a piece (1 cm) → that piece;
 *   - inside the room but in neither piece → 0, the chain's own body (dropped);
 *   - along the source outline but a little off it (a scanned wall can sit a
 *     centimetre or two away) → the nearer piece;
 *   - off the source outline too — a wall running across the mouth of a box
 *     carved out of the room, say — → parked with the room that keeps the id,
 *     exactly as it stood, and flagged so it is not joined onto a stretch that
 *     does lie on an outline.
 */
function pieceOfRun(mid, cut, sourceRing, fallbackSide) {
  const dl = distanceToPolygonBoundary(mid, cut.left.polygon);
  const dr = distanceToPolygonBoundary(mid, cut.right.polygon);
  // On the outline a chain end stands on — nearer it than to either piece.
  // Asked FIRST: a run of a few millimetres beside a face hit is within the
  // pieces' 1 cm membership tolerance, and joined onto that piece's wall it
  // would leave the wall poking past the piece's corner.
  const dGap = Math.min(
    ...(cut.gaps || []).map((g) => distanceToPolyline(mid, g))
  );
  if (dGap <= HIT_ON_WALL_M && dGap < Math.min(dl, dr)) {
    return { side: 0, onOutline: false };
  }
  const side = cut.sideOf(mid);
  if (side !== 0) return { side, onOutline: true };
  if (sourceRing.length >= 3 && isPointInsidePolygon(mid, sourceRing)) {
    return { side: 0, onOutline: false };
  }
  if (
    sourceRing.length >= 3 &&
    distanceToPolygonBoundary(mid, sourceRing) > HIT_ON_WALL_M
  ) {
    return { side: fallbackSide, onOutline: false };
  }
  return { side: dl <= dr ? 1 : -1, onOutline: true };
}

/** Shortest distance from `p` to an open polyline. */
function distanceToPolyline(p, line) {
  let best = Infinity;
  for (let i = 0; i + 1 < line.length; i++) {
    best = Math.min(best, distanceToSegment(p, line[i], line[i + 1]));
  }
  return best;
}

/**
 * Hand a room's walls to the two pieces of a chain split.
 *
 * A wall of the chain itself (`alignment.structureWallId` in the chain) goes
 * to `chainFaces`, for `buildChainFaces` to replace. Every other wall is cut at
 * each of the cut's four boundary hits that lies on it; each sub-interval goes
 * to the piece its midpoint is in, the slice between one chain end's two hits
 * (what the end stands on) and anything under `MIN_PIECE_M` are dropped, and
 * the longest surviving piece keeps the id.
 *
 * @param {object} room the room being split
 * @param {object} cut a `cutRoomAlongChain` result
 * @param {object} chain the `buildChain` result it was cut along
 * @param {() => string} genId mints the id of a clipped wall's other pieces
 * @param {1|-1} [fallbackSide] the piece a wall with no geometry at all is
 *   parked in — the one that keeps the room's id
 * @returns {{ left: object[], right: object[], chainFaces: object[],
 *             dropped: string[] }} copies throughout
 */
function assignRoomWallsToPieces(room, cut, chain, genId, fallbackSide = 1) {
  const left = [];
  const right = [];
  const chainFaces = [];
  const dropped = [];
  const chainIds = new Set(chain.wallIds);
  const sourceRing = roomFloorPolygonXZ(room);
  const push = (side, w) => (side === -1 ? right : left).push(w);

  for (const w of room?.walls || []) {
    if (!w) continue;
    if (chainIds.has(w.alignment?.structureWallId)) {
      chainFaces.push(cloneWall(w));
      continue;
    }
    const ends = wallEndpoints(w);
    if (!ends) {
      // Genuinely nothing to judge it by -- not even a centre and a length.
      // Never drop it: park it in the piece that keeps the room's id.
      push(fallbackSide, cloneWall(w));
      continue;
    }
    // A wall whose endpoints were DERIVED has no alignment corners to clip, so
    // give the pieces the corners its own pose implies. That is a repair of an
    // incomplete record, not an invention: the corners are exactly where the
    // wall's centre, rotation and length already say its ends are.
    const clipSource = ends.derived
      ? {
          ...w,
          alignment: {
            ...(w.alignment || {}),
            bottomLeft: ends.bl,
            bottomRight: ends.br,
          },
        }
      : w;
    const len = Math.hypot(ends.br.x - ends.bl.x, ends.br.z - ends.bl.z);
    const cuts = [0, ...cutsAlongWall(ends, cut, sourceRing), 1];
    const runs = [];
    for (let i = 0; i + 1 < cuts.length; i++) {
      const s0 = cuts[i];
      const s1 = cuts[i + 1];
      if (!((s1 - s0) * len > MIN_FACE_M)) continue;
      const tm = (s0 + s1) / 2;
      const mid = {
        x: ends.bl.x + (ends.br.x - ends.bl.x) * tm,
        z: ends.bl.z + (ends.br.z - ends.bl.z) * tm,
      };
      const { side, onOutline } = pieceOfRun(
        mid,
        cut,
        sourceRing,
        fallbackSide
      );
      if (side === 0) continue; // inside the chain's own body
      runs.push({ s0, s1, side, onOutline, len: (s1 - s0) * len });
    }
    const pieces = joinRuns(runs)
      .filter((r) => r.len >= MIN_PIECE_M)
      .map((r) => ({ s0: r.s0, s1: r.s1, side: r.side, pieceLen: r.len }));
    if (pieces.length === 0) {
      dropped.push(w.id);
      continue;
    }
    if (pieces.length === 1 && pieces[0].s0 === 0 && pieces[0].s1 === 1) {
      push(pieces[0].side, cloneWall(w));
      continue;
    }
    // Keep-one-id: the longest piece is the same wall, the others are new.
    const longest = pieces.reduce((a, b) => (b.pieceLen > a.pieceLen ? b : a));
    for (const pc of pieces) {
      const out = clipWall(
        clipSource,
        pc.s0,
        pc.s1,
        pc === longest ? w.id : genId()
      );
      if (w.texture)
        out.texture = clipWallTextureRegions(w.texture, pc.s0, pc.s1);
      push(pc.side, out);
    }
  }
  return { left, right, chainFaces, dropped };
}

/** The polygon edge lying on `c` (longest, if the cut grazed more than one). */
function edgeOnFaceLine(polygon, frame, c) {
  let best = null;
  let bestLen = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (Math.abs(u(a, frame) - c) > ON_LINE_EPS_M) continue;
    if (Math.abs(u(b, frame) - c) > ON_LINE_EPS_M) continue;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len > bestLen) {
      bestLen = len;
      best = { a, b, len };
    }
  }
  return best;
}

/** Existing long faces of the partition, keyed by the side they serve. */
function classifyPartitionFaces(faces, frame) {
  const out = { 1: null, '-1': null };
  for (const f of faces) {
    const ends = wallEndpoints(f);
    if (!ends) continue;
    const dx = ends.br.x - ends.bl.x;
    const dz = ends.br.z - ends.bl.z;
    const len = Math.hypot(dx, dz) || 1;
    const dot = (dx / len) * frame.dir.x + (dz / len) * frame.dir.z;
    if (Math.abs(dot) < LONG_FACE_DOT) continue; // an end cap, not a face
    const mid = {
      x: (ends.bl.x + ends.br.x) / 2,
      z: (ends.bl.z + ends.br.z) / 2,
    };
    const side = u(mid, frame) >= frame.cCenter ? 1 : -1;
    if (!out[side]) out[side] = f;
  }
  return out;
}

/**
 * A room wall laid exactly on `edge`, facing `inward`. Reuses the template
 * face's id and appearance when there is one, so a texture the user applied
 * survives.
 */
function faceOnEdge(edge, inward, template, wall, genId, seat = {}) {
  // Order the endpoints so perpDir(bottomLeft → bottomRight) points INTO the
  // room — the room-wall convention (`createRoomWallForEdge`).
  const dx = edge.b.x - edge.a.x;
  const dz = edge.b.z - edge.a.z;
  const perpDot = (-dz / edge.len) * inward.x + (dx / edge.len) * inward.z;
  const start = perpDot >= 0 ? edge.a : edge.b;
  const end = perpDot >= 0 ? edge.b : edge.a;

  // A face is seated on its ROOM's floor, which is the only Y the room's other
  // walls agree on. The structure wall's own `center.y` is NOT a safe stand-in:
  // it is whatever the placement gesture recorded, and a room whose floor sits
  // at y = -1.388 with a wall centred at y = 1.2 gets a face spanning 0 -> 2.4
  // -- starting 1.4 m above the floor and poking through the ceiling, which the
  // ceiling generator then conforms back down. The wall came out 1.112 m tall
  // instead of 2.5, and the take-off billed 38 sq ft of new wall where there
  // were 95. `seat` carries the room's floor and typical wall height; the
  // wall's own pose is the last resort.
  // HEIGHT comes from the wall itself: it is the user's own placement, and a
  // half-wall must stay a half-wall rather than being silently grown to the
  // room. The room's height is only the fallback. (In practice they agree --
  // `getWallHeightForPoint` creates a user wall at its room's height.)
  const baseHeight =
    template?.dimensions?.y || wall.dimensions?.y || seat.height || 0;
  const floorY =
    template?.alignment?.bottomLeft?.y ??
    seat.floorY ??
    (wall.center?.y ?? baseHeight / 2) - baseHeight / 2;
  // The TOP. A wall drawn to meet the ceiling carries an outline
  // (`chainWallsFromTaps`); its face meets the ceiling the split leaves in
  // place, read along the face's own line when the room's ceiling is handed
  // in (`seat.ceilings`) — where the ceiling steps between the wall's two
  // faces, each is under its own part. Else the outline itself, carried onto
  // this edge as heights above the floor: the template's (the face itself,
  // before the split), then the wall's. A wall with neither keeps the flat
  // top it always had.
  const own = profileOfOutline(template) || profileOfOutline(wall);
  const profile =
    own && seat.ceilings?.length && profileOfOutline(wall)
      ? profileUnderCeiling(seat.ceilings, start, end, {
          floorY,
          height: seat.height || profileTop(own),
        })
      : own;
  const height = profile ? profileTop(profile) : baseHeight;
  const centerY = profile
    ? floorY + height / 2
    : (template?.center?.y ?? floorY + height / 2);
  const rotationY = Math.atan2(-(end.z - start.z), end.x - start.x);
  const bl = { x: start.x, y: floorY, z: start.z };
  const br = { x: end.x, y: floorY, z: end.z };

  return {
    ...(template || {}),
    id: template?.id || genId(),
    center: {
      x: (start.x + end.x) / 2,
      y: centerY,
      z: (start.z + end.z) / 2,
    },
    dimensions: {
      ...(template?.dimensions || {}),
      x: edge.len,
      y: height,
      z: 0,
    },
    rotation: { x: 0, y: rotationY, z: 0 },
    polygonCorners: profile
      ? outlineCorners(profile, bl, br, floorY)
      : [
          { x: bl.x, y: floorY, z: bl.z },
          { x: br.x, y: floorY, z: br.z },
          { x: br.x, y: floorY + height, z: br.z },
          { x: bl.x, y: floorY + height, z: bl.z },
        ],
    alignment: {
      ...(template?.alignment || {}),
      structureWallId: wall.id,
      bottomLeft: bl,
      bottomRight: br,
      bottomMiddle: {
        x: (bl.x + br.x) / 2,
        y: floorY,
        z: (bl.z + br.z) / 2,
      },
    },
    isUserCreated: true,
    // A template's own trim rides the spread above; a face with none gets the
    // default, so a new partition wall has baseboard like any other.
    trim: template?.trim ?? { ...DEFAULT_WALL_TRIM },
  };
}

/**
 * A straight wall's room wall for one side, laid exactly on that side's new
 * boundary edge — the gap close's one face (`functions/features/room-gap.js`).
 *
 * @param {{x,z}[]} polygon the side's new floor polygon
 * @param {1|-1} side which face line this side's boundary sits on
 * @returns {object|null} null when the cut left no edge on the face line
 */
function buildPartitionFace(
  polygon,
  frame,
  side,
  template,
  wall,
  genId,
  seat = {}
) {
  const c = side === 1 ? frame.cFront : frame.cBack;
  const edge = edgeOnFaceLine(polygon, frame, c);
  if (!edge) return null;
  const inward = { x: side * frame.n.x, z: side * frame.n.z };
  return faceOnEdge(edge, inward, template, wall, genId, seat);
}

/**
 * The chain's room walls for ONE piece: one face per stretch, laid on the
 * piece's own edge on that stretch's face line (the trimmed face polyline the
 * piece was built from), facing into the piece. Each reuses the id and look
 * of the stretch's existing face on that side, when the wall had one. A wall
 * END the piece closes round (`cut.caps`) gets a face of its own.
 *
 * @param {object} cut a `cutRoomAlongChain` result
 * @param {object} chain the `buildChain` result
 * @param {1|-1} side 1 for the left piece, -1 for the right
 * @param {object[]} chainFaces the chain walls' existing faces in the room
 *   (`assignRoomWallsToPieces(...).chainFaces`)
 * @param {{ floorY?, height? }} seat the room's floor and typical height
 * @param {() => string} genId
 * @param {object[]} structureWalls the structure walls the chain is made of
 * @returns {object[]}
 */
function buildChainFaces(
  cut,
  chain,
  side,
  chainFaces,
  seat,
  genId,
  structureWalls
) {
  const face = side === 1 ? cut.faces.left : cut.faces.right;
  const byId = new Map((structureWalls || []).map((w) => [w?.id, w]));
  const out = [];
  chain.stretches.forEach((st, i) => {
    const a = face[i];
    const b = face[i + 1];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (!(len > MIN_FACE_M)) return;
    const wall = byId.get(st.wallId) || { id: st.wallId };
    const { dir, n } = stretchFrame(st);
    // The existing faces are classified in the WALL's own frame; the stretch
    // may run against the wall's stored direction.
    const own = wallDir(wall);
    const orient = own.x * dir.x + own.z * dir.z >= 0 ? 1 : -1;
    const mine = (chainFaces || []).filter(
      (f) => f?.alignment?.structureWallId === st.wallId
    );
    const template = wall.center
      ? classifyPartitionFaces(mine, wallFrame(wall))[side * orient]
      : null;
    const inward = { x: side * n.x, z: side * n.z };
    out.push(faceOnEdge({ a, b, len }, inward, template, wall, genId, seat));
  });
  // A capped end (the wall ends at an inside corner, and the piece closes
  // round it): the wall's END is an edge of this piece too, so it gets a face,
  // facing away from the chain — as a free end's cap does.
  const caps = (side === 1 ? cut.caps?.left : cut.caps?.right) || [];
  for (const cap of caps) {
    const len = Math.hypot(cap.b.x - cap.a.x, cap.b.z - cap.a.z);
    if (!(len > MIN_FACE_M)) continue;
    const wall = byId.get(cap.wallId) || { id: cap.wallId };
    out.push(
      faceOnEdge({ a: cap.a, b: cap.b, len }, cap.out, null, wall, genId, seat)
    );
  }
  return out;
}

module.exports = {
  cloneWall,
  ON_LINE_EPS_M,
  MIN_PIECE_M,
  LONG_FACE_DOT,
  HIT_ON_WALL_M,
  assignRoomWallsToPieces,
  edgeOnFaceLine,
  classifyPartitionFaces,
  buildPartitionFace,
  buildChainFaces,
};
